import { FormPage } from '@checkit/react-components';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import React, { FC, useCallback, useEffect, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps } from 'react-router-dom';

import { useAxiosDispatch } from '../../middleware/axios';
import { createSnackbar } from '../../notifications/actions';
import { createCheckpoint, isCreateCheckpointSuccess } from '../actions';
import { messages as messagesCommon } from '../common.translations';
import { messages as messagesNewCheckpoint } from '../new/NewCheckpoint.translations';
import { CheckpointDto } from '../state';

import BulkImportCheckpointForm from './BulkImportCheckpointForm';
import { messages } from './BulkImportCheckpoints.translations';
import BulkImportProgress from './BulkImportProgress';
import { bulkImportReducer, initialState } from './BulkImportReducer';

export const BulkImportCheckpoints: FC<RouteComponentProps> = ({ history }) => {
  const { formatMessage } = useIntl();
  const dispatch = useAxiosDispatch( );

  const [checkpoints, setCheckpoints] = useState<CheckpointDto[]>([]);
  const [state, dispatchProgress] = useReducer(bulkImportReducer, initialState);

  useEffect(() => {
    if (checkpoints.length > 0) {
      dispatchProgress({ type: 'START', checkpoints });
    }
  }, [checkpoints]);

  const formatErrorMessage = useCallback((type: string): string => {
    switch (type) {
      case messagesCommon.CHECKPOINT_NAME_NOT_UNIQUE.id:
        return formatMessage(messagesCommon.CHECKPOINT_NAME_NOT_UNIQUE);
      case messagesCommon.QR_CODE_NOT_UNIQUE.id:
        return formatMessage(messagesCommon.QR_CODE_NOT_UNIQUE);
      default:
        return formatMessage(messagesNewCheckpoint['checkpoint.new.error']);
    }
  }, [formatMessage]);

  useEffect(() => {
    if (state.currentCheckpoint !== undefined) {
      dispatch(createCheckpoint(state.checkpoints[state.currentCheckpoint]))
        .then((action) => {
          if (isCreateCheckpointSuccess(action)) {
            dispatchProgress({ type: 'UPDATE_PROGRESS', success: true, id: state.currentCheckpoint });
          } else {
            const errorType = action.error ? (action as any).error.response.data.message : null;
            dispatchProgress({
              id: state.currentCheckpoint,
              message: formatErrorMessage(errorType),
              success: false,
              type: 'UPDATE_PROGRESS'
            });
          }
        });
    }
  }, [state.currentCheckpoint, dispatch, state.checkpoints, formatErrorMessage, history]);

  const showProgress = state.status !== 'PENDING';

  function handleConfirm() {
    setCheckpoints([]);
    dispatchProgress({ type: 'CANCEL' });
  }

  function handleCancel() {
    handleConfirm();
    dispatch(createSnackbar({
      message: formatMessage(messages['checkpoint.bulkImport.cancelImportMessage']),
      options: {
        variant: 'error'
      }
    }));

  }

  return (
    <FormPage
      pageTitle={formatMessage(messages['checkpoint.bulkImport.title'])}
      pageIcon={<CloudUploadIcon />}
    >
      {showProgress ? (
        <BulkImportProgress
          progressReports={state.importProgress}
          handleCancel={handleCancel}
          handleConfirm={handleConfirm}
        />
      ) :
        <BulkImportCheckpointForm onImport={setCheckpoints} />
      }
    </FormPage>
  );
};
